<template>
    <layout>
        <template v-slot:header>
            <h2>Mobile Tool Distributor {{ form.first_name }} {{ form.last_name }} ({{ user.status_desc }})</h2>
            <AppHead :title="`Mobile Tool Distributor ${form.first_name} ${form.last_name} (${this.user.status_desc})`" />
            <BreadCrumbs activeItem="MTD" :crumbs="{ home: 'Dashboard', mtd: 'MTDs' }" />
            <flash-messages />
        </template>

        <template v-slot:default>
            <trashed-message v-if="user.deleted_at" class="mb-6" @restore="restore">
                This user has been deleted.
            </trashed-message>

            <div class="row">
                <div class="col-12 mb-3">
                    <inertia-link :href="route('mtd.edit', user.id)" tabindex="-1">
                        <jet-button class="btn-short text-nowrap m-1" btnStyle="dark" icon="fas fa-edit">Edit</jet-button>
                    </inertia-link>

                    <jet-button v-if="user.pending" class="btn-short text-nowrap m-1" icon="fas fa-thumbs-up"
                        :disabled="formApprove.processing" @click="approve()">Approve</jet-button>

                    <jet-button v-if="user.mtd_status == 3" class="btn-short text-nowrap m-1" icon="fas fa-share"
                        @click="resend()">Resend Invite</jet-button>
                    <span v-if="user.active">
                        <inertia-link :href="route('mtd.shopas', user.id)" tabindex="-1">
                            <jet-button class="btn-short text-nowrap m-1" btnStyle="dark" icon="fas fa-sign-in-alt">Login
                                As</jet-button>
                        </inertia-link>

                        <inertia-link :href="route('admin.mtd.shop', user.id)" tabindex="-1">
                            <jet-button class="btn-short text-nowrap m-1" btnStyle="dark"
                                icon="fas fa-shopping-cart">Shop</jet-button>
                        </inertia-link>

                        <inertia-link :href="route('admin.orders.usership', user.id)" tabindex="-1">
                            <jet-button class="btn-short text-nowrap m-1" btnStyle="dark" icon="fas fa-shipping-fast">Ship
                                Orders</jet-button>
                        </inertia-link>

                        <inertia-link :href="route('admin.returns.mtd', user.id)" tabindex="-1">
                            <jet-button class="btn-short text-nowrap m-1" btnStyle="dark"
                                icon="fas fa-rotate-left">Returns</jet-button>
                        </inertia-link>

                        <jet-button class="btn-short text-nowrap m-1" icon="fas fa-key" @click="sendReset">Send Password
                            Reset</jet-button>
                    </span>

                    <jet-button v-if="user.is_role_mtd" class="btn-short text-nowrap m-1" icon="fas fa-sync"
                        :loading="formSyncProducts.processing" :disabled="formSyncProducts.processing"
                        @click="syncProducts()">Sync Products</jet-button>

                    <inertia-link :href="route('export.accountActivityReport', user.id)" tabindex="-1">
                        <jet-button class="btn-short text-nowrap m-1" btnStyle="dark" icon="fas fa-chart">Generate Account
                            Activity Report</jet-button>
                    </inertia-link>

                </div>

                <div class="col-md-4 col-sm-6 mb-3">
                    <card headerStyle="primary" title="MTD Info">
                        <ul class="list-unstyled">
                            <li class="d-flex justify-content-between">Company: <span class="text-secondary text-right">{{
                                form.company_name }}</span></li>
                            <li class="d-flex justify-content-between">Email: <span class="text-secondary text-right">
                                    <Email v-model="form.email" />
                                </span></li>
                            <li class="d-flex justify-content-between">Phone: <span class="text-secondary text-right">
                                    <Phone v-model="user.phone_number" />
                                </span></li>
                            <li class="d-flex justify-content-between">Service Area: <span
                                    class="text-secondary text-right">{{ form.mtd_service_area }}</span></li>
                            <li class="d-flex justify-content-between">Status: <span class="text-secondary text-right">{{
                                user.status_desc }}</span></li>
                            <li class="d-flex justify-content-between">RSM: <span class="text-secondary text-right">{{
                                user.rsm_name }}</span></li>
                            <li v-if="user.stripe_id" class="d-flex justify-content-between">Stripe Customer: <a
                                    v-bind:href="user.stripe_customer_url" target="_blank">
                                    <span class="text-right">{{ user.stripe_id }}</span></a></li>
                            <li v-if="user.stripe_connect_id" class="d-flex justify-content-between">Stripe Connect: <a
                                    v-bind:href="user.stripe_connect_url" target="_blank">
                                    <span class="text-right">{{ user.stripe_connect_id }}</span></a></li>
                        </ul>

                        <hr>

                        <jet-input id="password" type="password" class="form-control" v-model="formPass.password" />
                        <jet-input-error :message="formPass.errors.password" class="mt-2" />

                        <jet-button class="btn-short mt-2" icon="fas fa-key" @click="setPass()">Set Temporary
                            Password</jet-button>

                        <hr />

                        <br>
                        <div v-if="user.tax_exempt">
                            <i class="mr-3 fa fa-check-circle step-check step-check-complete"></i> Tax Exempt
                            <div v-if="files.length == 0" class="alert alert-danger">Need Tax Exempt Form</div>
                        </div>
                        <div v-else>
                            Tax Exempt Pending Document Review

                            <jet-button class="btn-short mt-2 mt-lg-0 ml-0 ml-lg-2" icon="fas fa-check"
                                @click="setTaxExempt()">Approve Tax Exempt</jet-button>
                        </div>

                        <ul v-for="file in files" :key="file.date" class="list-group">
                            <li class="list-group-item list-group-item-info px-2 py-1">
                                <a class="text-nowrap" :href="file.url" target="_new">Uploaded on {{
                                    moment(file.date).format('M/D/YY h:mm A') }}</a>
                            </li>
                        </ul>

                        <jet-button class="btn-short mt-2 mt-lg-0" icon="fas fa-share"
                            @click.prevent="selectDocument">Upload Tax Exemption Document</jet-button>
                        <input type="file" hidden ref="document" @change="uploadDocument">
                        <jet-input-error :message="form.errors.document" class="mt-2" />

                    </card>
                </div>

                <div class="col-md-4 col-sm-6 mb-3">
                    <card headerStyle="primary" title="Billing">
                        <ul class="list-unstyled">
                            <li class="d-flex justify-content-between">Account Balance: <span
                                    class="text-secondary text-right">{{ (user.account_balance < 0 ? '-$' : '$') +
                                        formatCurrency(Math.abs(user.account_balance)) }}</span>
                            </li>
                            <li class="d-flex justify-content-between">Due Amount: <span
                                    class="text-secondary text-right">{{ formatDollars(user.total_due) }}</span></li>
                            <li class="d-flex justify-content-between">Last Payment:
                                <span v-if="payments.data.length" class="text-secondary text-right">
                                    {{ formatDollars(payments.data[0].total) }} on
                                    {{ moment(payments.data[0].transaction_date).format('M/D/YY h:mm A') }}
                                </span>
                            </li>
                        </ul>
                    </card>
                    <br />
                    <EditTags :tags="tags" :savedTags="user.tags" :userIds="[user.id]"></EditTags>

                    <card title="Multitruck - Linked Accounts">

                        <ul class="list-unstyled">
                            <li v-for="linked in user.linked">

                                <p class="mb-0 font-weight-bold">Company</p>
                                <p class="mb-0 text-secondary"><inertia-link :href="route('mtd.view', linked.id)">{{
                                    linked.company_name }}</inertia-link></p>
                                <p class="mb-0 font-weight-bold">User Phone Number:</p>
                                <p class="mb-0 text-secondary">
                                    <Phone v-model="linked.phone_number" />
                                </p>
                            </li>
                        </ul>
                    </card>
                </div>

                <div class="col-md-4 col-sm-6 mb-3" v-if="notes.length">
                    <card :title="notes.length + ' Notes'">
                        <div class="sticky-note mb-3 w-100" v-for="note in notes" :key="note.id">
                            <div class="header bg-light border py-1 px-2">
                                <strong>By: {{ note.user }} on {{ moment(note.date).format('M/D/YY h:mm A') }}</strong>
                            </div>

                            <div class="body p-2">
                                <p class="m-0">{{ note.content }}</p>
                            </div>
                        </div>
                    </card>
                </div>

            </div>

            <div class="row">
                <div class="col-md-6 mb-3">
                    <card headerStyle="primary" title="Orders" class="mb-3">
                        <DataTable v-if="orders.data.length || loadings.orders" :value="orders.data" :paginator="true"
                            :rowsPerPageOptions="[10, 20, 50, 100]"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="({first} - {last} of {totalRecords})" paginatorPosition="both"
                            sortField="date" :sortOrder="-1" class="p-datatable-sm admin-datatable thead-ui" dataKey="id"
                            :lazy="true" :loading="loadings.orders" :first="(orders.current_page - 1) * orders.per_page"
                            :rows="orders.per_page" :totalRecords="orders.total" @page="onPage('orders', $event)"
                            @row-click="(e) => this.$inertia.visit(route('admin.order.view', [e.data.id]))">
                            <Column field="date" header="Date" headerStyle="width: 3em">
                                <template #body="slotProps">
                                    {{ moment(slotProps.data.date).format('M/D/YY h:mm A') }}
                                </template>
                            </Column>
                            <Column field="id" header="Order #" headerStyle="width: 3em">
                                <template #body="slotProps">
                                    #{{ slotProps.data.id }}
                                    <PaymentMethodDisplay class="ml-2" :item="slotProps.data.payment.payment_method" />
                                </template>
                            </Column>
                            <Column field="status_desc" header="Status" headerStyle="width: 3em"></Column>
                            <Column field="total" header="Total" headerStyle="width: 3em">
                                <template #body="slotProps">
                                    {{ formatDollars(slotProps.data.total) }}
                                </template>
                            </Column>
                        </DataTable>
                        <span v-if="!orders.data.length && !loadings.orders">No Orders</span>
                    </card>
                    <card headerStyle="primary" title="Pending Shipments" class="mb-3">
                        <DataTable v-if="pending_shipments.length" :value="pending_shipments" :paginator="true" :rows="10"
                            :rowsPerPageOptions="[10, 20, 50, 100]"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="({first} - {last} of {totalRecords})" paginatorPosition="both"
                            sortField="date" :sortOrder="-1" class="p-datatable-sm admin-datatable thead-ui" dataKey="id">
                            <Column field="id" header="Order #">
                                <template #body="slotProps">
                                    #{{ slotProps.data.id }}
                                    <PaymentMethodDisplay class="ml-1" :item="slotProps.data.payment.payment_method" />
                                </template>
                            </Column>
                            <Column header="Date" headerStyle="width: 3em">
                                <template #body="slotProps">
                                    {{ moment(slotProps.data.date).format('M/D/YY h:mm A') }}
                                </template>
                            </Column>
                            <Column field="sku" header="SKU"></Column>
                            <Column field="name" header="Name"></Column>
                            <Column field="qty" header="Quantity"></Column>
                            <Column field="status" header="Status"></Column>
                        </DataTable>
                        <span v-if="!pending_shipments.length && !loadings.pending_shipments">No Pending Shipments</span>
                    </card>
                </div>

                <div class="col-md-6 mb-3">
                    <card headerStyle="primary" title="Payments">
                        <DataTable :value="payments.data" :paginator="true" :rowsPerPageOptions="[10, 20, 50, 100]"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="({first} - {last} of {totalRecords})" paginatorPosition="both"
                            sortField="transaction_date" :sortOrder="-1" class="p-datatable-sm admin-datatable thead-ui"
                            :lazy="true" :loading="loadings.payments"
                            :first="(payments.current_page - 1) * payments.per_page" :rows="payments.per_page"
                            :totalRecords="payments.total" @page="onPage('payments', $event)"
                            @row-click="(e) => this.openPrintPaymentReceipt([e.data.id])" dataKey="id">
                            <Column header="ID">
                                <template #body="slotProps">
                                    {{ slotProps.data.id }}
                                </template>
                            </Column>
                            <Column header="Date">
                                <template #body="slotProps">
                                    {{ moment(slotProps.data.transaction_date).format('M/D/YY h:mm A') }}
                                </template>
                            </Column>
                            <Column header="Type">
                                <template #body="slotProps">
                                    <label v-if="slotProps.data.payment_method?.cc_image_url">
                                        <img :src="slotProps.data.payment_method.cc_image_url"
                                            alt="{{ slotProps.data.payment_method.brand }}" width="32" />
                                        <span class="text-secondary text-size-small"> ending in {{
                                            slotProps.data.payment_method.last4 }}</span>
                                    </label>
                                    <span v-else>{{ slotProps.data.description }}</span>
                                </template>
                            </Column>
                            <Column header="Amount">
                                <template #body="slotProps">
                                    {{ formatDollars(slotProps.data.amount) }}
                                </template>
                            </Column>
                            <Column header="Total">
                                <template #body="slotProps">
                                    {{ formatDollars(slotProps.data.total) }}
                                </template>
                            </Column>
                        </DataTable>
                        <span v-if="!payments.data.length && !loadings.payments">No Payments</span>
                    </card>
                    <br v-if="payments.data.length" />
                    <card headerStyle="primary" title="Enter Payment">
                        <div class="form-group">
                            <jet-label for="payment_intent" value="Type:" /> &nbsp;

                            <input type="radio" name="payment_intent" v-model="formPayment.payment_intent" value="Check"
                                id="payment_type_check" />
                            <label class="form-check-label ml-1" :for="payment_type_check">Check</label>
                            &nbsp;
                            <input type="radio" name="payment_intent" v-model="formPayment.payment_intent" value="Cash"
                                id="payment_type_cash" />
                            <label class="form-check-label ml-1" :for="payment_type_cash">Cash</label>
                            &nbsp;
                            <input type="radio" name="payment_intent" v-model="formPayment.payment_intent"
                                value="Adjustment" id="payment_type_adjustment" />
                            <label class="form-check-label ml-1" :for="payment_type_adjustment">Adjustment</label>
                        </div>

                        <div class="row">
                            <div class="col-6">
                                <jet-label for="payment_amount" value="Amount" />
                                <AmountInput inputId="payment_amount" v-model="formPayment.payment_amount" />
                                <jet-input-error :message="formPayment.errors.payment_amount" class="mt-2" />
                            </div>

                            <div class="col-6" v-if="formPayment.payment_intent == 'Check'">
                                <jet-label for="check_number" value="Check number" />
                                <jet-input id="check_number" type="text" class="form-control"
                                    v-model="formPayment.check_number" />
                                <jet-input-error :message="formPayment.errors.check_number" class="mt-2" />
                            </div>
                        </div>

                        <div class="row mb-3">
                            <div class="col-6">
                                <jet-label for="order_id" value="Order Id" />
                                <input id="order_id" type="number" step="1" class="form-control" :min="0"
                                    v-model="formPayment.order_id" />
                                <jet-input-error :message="formPayment.errors.order_id" class="mt-2" />
                            </div>

                            <div class="col-6">
                                <jet-label for="order_shipment_id" value="Order Shipment Id" />
                                <input id="order_shipment_id" type="number" step="1" class="form-control" :min="0"
                                    v-model="formPayment.order_shipment_id" />
                                <jet-input-error :message="formPayment.errors.order_shipment_id" class="mt-2" />
                            </div>
                        </div>

                        <div class="form-group">
                            <jet-label for="payment_note" value="Customer Visible Note" />
                            <jet-input id="payment_note" type="text" class="form-control"
                                v-model="formPayment.payment_note" />
                            <jet-input-error :message="formPayment.errors.payment_note" class="mt-2" />
                        </div>

                        <div class="form-group">
                            <jet-label for="payment_admin_note" value="Admin Visible Note" />
                            <jet-input id="payment_admin_note" type="text" class="form-control"
                                v-model="formPayment.payment_admin_note" />
                            <jet-input-error :message="formPayment.errors.payment_admin_note" class="mt-2" />
                        </div>

                        <jet-button class="btn-short mt-2" @click="enterPayment()">Create Payment</jet-button>
                    </card>
                    <br />
                </div>
            </div>

            <card title="Sales by Product" class="mt-3" :collapse="true">
                <DataTable :value="sales.data" :paginator="true" sortField="qty" :sortOrder="-1"
                    class="p-datatable-sm admin-datatable thead-ui" :lazy="true" :loading="loadings.sales"
                    :first="(sales.current_page - 1) * sales.per_page" :rows="sales.per_page" :totalRecords="sales.total"
                    :rowsPerPageOptions="[10, 20, 50, 100]"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="({first} - {last} of {totalRecords})" @sort="onSortSales($event)"
                    @page="onPage('sales', $event)" dataKey="sku"
                    @row-click="(e) => this.$inertia.visit(route('admin.products.view', [e.data.product.id]))">
                    <Column field="sku" header="Product" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="qty" header="Total" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="qty_30" header="30" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="qty_60" header="60" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="qty_90" header="90" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="qty_120" header="120" headerStyle="width: 3em" :sortable="true"></Column>
                    <Column field="product.current_inventory" header="Inventory" headerStyle="width: 3em"></Column>
                </DataTable>
            </card>

            <Notes @addNote="addNote" :notes="notes" :formNote="formNote">
                <div class="form-group form-check">
                    <input type="checkbox" v-model="formNote.visible_mtd" class="form-check-input" true-value="1"
                        false-value="0" /> Visible to MTD
                    <div v-if="formNote.visible_mtd">
                        <input type="checkbox" v-model="formNote.notify_mtd" class="form-check-input" true-value="1"
                            false-value="0" /> Notify MTD
                    </div>
                </div>
            </Notes>
        </template>
    </layout>
    <teleport to="body">
        <PrintModal :printUrl="printUrl" @print="printPaymentReceipt" />
    </teleport>
</template>

<script>
import AppHead from '@/Shared/AppHead'
import Layout from '@/Shared/Admin/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import TrashedMessage from '@/Shared/TrashedMessage'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import Notes from '@/Shared/Notes'
import moment from 'moment'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import AmountInput from '@/Shared/AmountInput'
import FlashMessages from '@/Shared/FlashMessages'
import EditTags from "@/Shared/EditTags";
import PaymentType from '@/Components/Transaction/PaymentType'
import PaymentMethodDisplay from '@/Components/Transaction/PaymentMethodDisplay'
import PrintModal from '@/Components/Modals/PrintModal'

export default {
    components: {
        EditTags,
        AppHead,
        SelectInput,
        TextInput,
        TrashedMessage,
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetCheckbox,
        JetInputError,
        JetLabel,
        Layout,
        Notes,
        BreadCrumbs,
        AmountInput,
        FlashMessages,
        PaymentType,
        PaymentMethodDisplay,
        PrintModal,
    },
    props: {
        user: Object,
        rsms: Object,
        notes: Array,
        files: Array,
        tags: Array,
        sales: {
            type: Object,
            default: {
                data: [],
                per_page: 50,
                current_page: 0,
                total: 0,
            }
        },
        payments: {
            type: Object,
            default: {
                data: [],
                per_page: 10,
                current_page: 0,
                total: 0,
            }
        },
        orders: {
            type: Object,
            default: {
                data: [],
                per_page: 10,
                current_page: 0,
                total: 0,
            }
        },
        pending_shipments: {
            type: Array,
            default: []
        },
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                _method: 'put',
                first_name: this.user.first_name,
                last_name: this.user.last_name,
                email: this.user.email,
                company_name: this.user.company_name,
                mtd_service_area: this.user.mtd_service_area,
                address1: this.user.address_bill ? this.user.address_bill.address1 : null,
                address2: this.user.address_bill ? this.user.address_bill.address2 : null,
                city: this.user.address_bill ? this.user.address_bill.city : null,
                state: this.user.address_bill ? this.user.address_bill.state : null,
                postcode: this.user.address_bill ? this.user.address_bill.postcode : null,
                ship_address1: this.user.address_ship ? this.user.address_ship.ship_address1 : null,
                ship_address2: this.user.address_ship ? this.user.address_ship.ship_address2 : null,
                ship_city: this.user.address_ship ? this.user.address_ship.ship_city : null,
                ship_state: this.user.address_ship ? this.user.address_ship.ship_state : null,
                ship_postcode: this.user.address_ship ? this.user.address_ship.ship_postcode : null,
                mtd_assigned_rsm_id: this.user.address_ship ? this.user.mtd_assigned_rsm_id : null,
            }),
            formNote: this.$inertia.form({
                _method: 'put',
                content: null,
                visible_mtd: 0,
                notify_mtd: 0
            }),
            formDocument: this.$inertia.form({
                _method: 'put',
                document: null
            }),
            formPass: this.$inertia.form({
                _method: 'put',
                password: null
            }),
            formPayment: this.$inertia.form({
                _method: 'put',
                payment_intent: 'Check',
                payment_amount: null,
                check_number: null,
                order_id: null,
                order_shipment_id: null,
                payment_note: null,
                payment_admin_note: null,
            }),
            formApprove: this.$inertia.form({
                _method: 'put'
            }),
            formSyncProducts: this.$inertia.form({
            }),
            loadings: {
                orders: false,
                payments: false,
                sales: false,
            },
            sorts: {
                sales: undefined,
            },
            selectedReceiptPaymentId: null,
            printUrl: '',
        }
    },
    created() {
        this.moment = moment;
    },
    async mounted() {
        await this.onPage('orders', { page: 0, rows: 10 });
        await this.onPage('payments', { page: 0, rows: 10 });
        await this.onPage('sales', { page: 0, rows: 10 });
        await this.$inertia.form({}).post(this.route('mtd.view', this.user.id), {
            preserveState: true,
            preserveScroll: true,
            resetOnSuccess: true,
            only: ['pending_shipments'],
            onSuccess: () => {
            },
            onError: () => {
            },
        });
    },
    methods: {
        async onPage(table, event, params = {}) {
            this.loadings[table] = true
            const data = { ...params };
            data[`${table}_pagination`] = { page: event.page + 1, per_page: event.rows }
            if (this.sorts[table]) {
                data[`${table}_sort`] = this.sorts[table]
            }
            return new Promise((resolve, reject) => {
                this.$inertia.form(data).post(this.route('mtd.view', this.user.id), {
                    preserveState: true,
                    preserveScroll: true,
                    resetOnSuccess: true,
                    only: [table],
                    onSuccess: () => {
                        this.loadings[table] = false
                        resolve()
                    },
                    onError: () => {
                        this.loadings[table] = false
                        resolve()
                    },
                });
            })
        },
        onSortSales(event) {
            this.sorts.sales = (event.sortOrder < 0 ? '-' : '') + event.sortField
            this.onPage('sales', { page: this.sales.current_page - 1, rows: this.sales.per_page }, { sales_sort: this.sorts.sales })
        },
        update() {
            this.form.post(this.route('mtd.update', this.user.id))
        },
        destroy() {
            if (confirm('Are you sure you want to delete this user?')) {
                this.$inertia.delete(this.route('users.destroy', this.user.id))
            }
        },
        restore() {
            if (confirm('Are you sure you want to restore this user?')) {
                this.$inertia.put(this.route('users.restore', this.user.id))
            }
        },
        approve() {
            this.formApprove.post(this.route('mtd.approve', this.user.id))
        },
        syncProducts() {
            this.formSyncProducts.post(this.route('mtd.syncProducts', this.user.id))
        },
        resend() {
            this.$inertia.put(this.route('mtd.resend', this.user.id))
        },
        setTaxExempt() {
            this.$inertia.put(this.route('mtd.taxexempt', this.user.id))
        },
        markDone() {
            this.$inertia.put(this.route('mtd.markdone', this.user.id))
        },
        addNote() {
            this.formNote.post(this.route('mtd.addnote', this.user.id), {
                onSuccess: () => this.formNote.reset('content', 'visible_mtd', 'notify_mtd'),
            })
        },
        sendReset() {
            this.$inertia.visit(this.route('admin.users.resetlink', this.user.id));
        },
        setPass() {
            this.$confirm.require({
                message: 'Are you sure?',
                header: 'Set Temporary Password',
                icon: null,
                acceptLabel: 'Save',
                rejectLabel: 'Cancel',
                accept: () => {
                    this.formPass.post(this.route('mtd.setpass', this.user.id), {
                        onSuccess: () => this.formPass.reset('password'),
                    })
                },
                reject: () => {
                    //callback to execute when user rejects the action
                }
            });
        },
        enterPayment() {
            this.$confirm.require({
                header: 'Confirm Amount',
                message: 'Amount: $' + this.formatCurrency(this.formPayment.payment_amount),
                icon: null,
                acceptLabel: 'Confirm',
                rejectLabel: 'Cancel',
                accept: () => {
                    this.formPayment.post(this.route('admin.mtd.createAdminPayment', this.user.id))
                },
                reject: () => {
                    //callback to execute when user rejects the action
                }
            });
        },
        uploadDocument() {
            if (this.$refs.document) {
                this.formDocument.document = this.$refs.document.files[0]

                this.formDocument.post(route('admin.mtd.addfile', this.user), {
                    preserveScroll: true
                });
            }
        },
        selectDocument() {
            this.$refs.document.click();
        },
        toggleUseCustomerDirect() {
            this.$inertia.form({
                _method: 'put',
                mtd_use_customer_direct: !this.user.mtd_use_customer_direct,
            }).post(route('admin.mtd.updateMtdFeatures', this.user.id), {
                preserveScroll: true
            });
        },
        openPrintPaymentReceipt(paymentId) {
            this.selectedReceiptPaymentId = paymentId
            this.printUrl = this.route('tu.payment.receipt', this.selectedReceiptPaymentId);
            $('#PrintModal').modal('show');
        },
        printPaymentReceipt() {
            this.printUrl = this.route('tu.payment.receipt', { payment: this.selectedReceiptPaymentId, print: 1 });
        },
    },
}
</script>
